/** @file Shared state of a tree panel (hierarchy, assets): open rows, rename, menu, delete dialog, status. */
import { useState } from 'react';
import type { CommandCall } from '@pose2d/format/types';
import { focusStore, type InspectorFocus } from '../stores/focusStore';
import type { Point } from '../ui-kit/logic/placeMenu';
import type { DeleteTarget } from './deletePlan';
import type { RowAction, RowFocus } from './rowAction';
import { submitCalls, type SubmitResult } from './submitCalls';

/** The entity to focus once the batch is through: a known one as is, a created one by the id the dispatch answered. */
function resolveFocus(focus: RowFocus | null, result: SubmitResult): InspectorFocus | null {
  if (focus === null || !('created' in focus)) return focus;
  const id = result.created?.[focus.created];
  return id === undefined ? null : { kind: focus.kind, id };
}

/** `perform` runs a menu action; `tree` holds the Tree props every tree panel wires the same way. */
export function useTreePanel(initialExpanded: () => ReadonlySet<string>) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(initialExpanded);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ row: string; at: Point } | null>(null);
  const [deleting, setDeleting] = useState<readonly DeleteTarget[] | null>(null);
  const [status, setStatus] = useState<SubmitResult | null>(null);
  const run = (calls: CommandCall[]): Promise<SubmitResult> => submitCalls(calls).then((r) => { setStatus(r); return r; });
  const perform = (action: RowAction | null, row: string): void => {
    if (action?.kind === 'rename') setRenaming(row);
    if (action?.kind === 'delete') setDeleting([action.target]);
    if (action?.kind !== 'calls') return;
    const { expand, focus } = action;
    if (expand !== null) setExpanded(new Set([...expanded, expand]));
    void run(action.calls).then((r) => {
      const target = r.error === null ? resolveFocus(focus, r) : null;
      if (target !== null) focusStore.setState({ focus: target });
    });
  };
  const tree = {
    expanded, renamingId: renaming,
    onToggle: (id: string, open: boolean): void => { const next = new Set(expanded); if (open) next.add(id); else next.delete(id); setExpanded(next); },
    onRenameCancel: (): void => { setRenaming(null); },
    onContextMenu: (id: string, at: Point): void => { setMenu({ row: id, at }); },
  };
  return { renaming, setRenaming, menu, setMenu, deleting, setDeleting, status, setStatus, run, perform, tree };
}

/** The state useTreePanel returns. */
export type TreePanel = ReturnType<typeof useTreePanel>;
